import React, { useState, useEffect } from 'react';
import apiClient from '../../apiClient';
import { FaTimes, FaUser, FaUserTie, FaComments, FaCog, FaTag } from 'react-icons/fa';
import './StakeholderProfileModal.css';

function StakeholderProfileModal({ isOpen, stakeholder, onClose, apiBaseUrl }) {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    job_title: '',
    department: '',
    phone: '',
    preferred_contact_method: 'email',
    organization: '',
    location: '',
    stakeholder_category: 'internal',
    stakeholder_type: '',
    influence_level: '',
    interest_level: '',
    attitude: '',
    power_level: '',
    communication_preference: '',
    engagement_frequency: '',
    key_concerns: '',
    value_proposition: '',
    gender: '',
    age_range: '',
    tenure: '',
    custom_tags: [],
    custom_attributes: {},
    notes: ''
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentTagInput, setCurrentTagInput] = useState('');
  const [customAttrKey, setCustomAttrKey] = useState('');
  const [customAttrValue, setCustomAttrValue] = useState('');
  const [activeTab, setActiveTab] = useState('profile');

  // Tab configuration
  const tabs = [
    { id: 'profile', label: 'Profile', icon: FaUser },
    { id: 'classification', label: 'Classification', icon: FaUserTie },
    { id: 'engagement', label: 'Engagement', icon: FaComments },
    { id: 'additional', label: 'Additional', icon: FaCog }
  ];

  // Options for dropdowns
  const contactMethods = ['email', 'phone', 'meeting', 'written'];
  const stakeholderCategories = ['internal', 'external'];
  const stakeholderTypes = ['employee', 'customer', 'vendor', 'partner', 'regulatory', 'community'];
  const influenceLevels = ['high', 'medium', 'low'];
  const interestLevels = ['high', 'medium', 'low'];
  const attitudeOptions = ['supporter', 'neutral', 'resistant'];
  const powerLevels = ['high', 'medium', 'low'];
  const engagementFrequencies = ['daily', 'weekly', 'monthly', 'quarterly'];
  const ageRanges = ['18-25', '26-35', '36-45', '46-55', '56-65', '65+'];
  const tenureOptions = ['0-1 years', '1-5 years', '5-10 years', '10+ years'];

  useEffect(() => {
    console.log('StakeholderProfileModal useEffect triggered', { stakeholder, isOpen });
    
    if (stakeholder) {
      console.log('Stakeholder data received:', stakeholder);
      
      const newFormData = {
        first_name: stakeholder.first_name || '',
        last_name: stakeholder.last_name || '',
        email: stakeholder.email || '',
        job_title: stakeholder.job_title || '',
        department: stakeholder.department || '',
        phone: stakeholder.phone || '',
        preferred_contact_method: stakeholder.preferred_contact_method || 'email',
        organization: stakeholder.organization || '',
        location: stakeholder.location || '',
        stakeholder_category: stakeholder.stakeholder_category || 'internal',
        stakeholder_type: stakeholder.stakeholder_type || '',
        influence_level: stakeholder.influence_level || '',
        interest_level: stakeholder.interest_level || '',
        attitude: stakeholder.attitude || '',
        power_level: stakeholder.power_level || '',
        communication_preference: stakeholder.communication_preference || '',
        engagement_frequency: stakeholder.engagement_frequency || '',
        key_concerns: stakeholder.key_concerns || '',
        value_proposition: stakeholder.value_proposition || '',
        gender: stakeholder.gender || '',
        age_range: stakeholder.age_range || '',
        tenure: stakeholder.tenure || '',
        custom_tags: stakeholder.custom_tags || [],
        custom_attributes: stakeholder.custom_attributes || {},
        notes: stakeholder.notes || ''
      };
      
      console.log('Setting form data:', newFormData);
      setFormData(newFormData);
    } else {
      console.log('No stakeholder provided, resetting form for new stakeholder');
      // Reset form for new stakeholder
      setFormData({
        first_name: '',
        last_name: '',
        email: '',
        job_title: '',
        department: '',
        phone: '',
        preferred_contact_method: 'email',
        organization: '',
        location: '',
        stakeholder_category: 'internal',
        stakeholder_type: '',
        influence_level: '',
        interest_level: '',
        attitude: '',
        power_level: '',
        communication_preference: '',
        engagement_frequency: '',
        key_concerns: '',
        value_proposition: '',
        gender: '',
        age_range: '',
        tenure: '',
        custom_tags: [],
        custom_attributes: {},
        notes: ''
      });
    }
    // Reset to first tab when modal opens
    setActiveTab('profile');
  }, [stakeholder, isOpen]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleAddTag = () => {
    if (currentTagInput.trim() && !formData.custom_tags.includes(currentTagInput.trim())) {
      setFormData(prev => ({
        ...prev,
        custom_tags: [...prev.custom_tags, currentTagInput.trim()]
      }));
      setCurrentTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setFormData(prev => ({
      ...prev,
      custom_tags: prev.custom_tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const handleAddCustomAttribute = () => {
    if (customAttrKey.trim() && customAttrValue.trim()) {
      setFormData(prev => ({
        ...prev,
        custom_attributes: {
          ...prev.custom_attributes,
          [customAttrKey.trim()]: customAttrValue.trim()
        }
      }));
      setCustomAttrKey('');
      setCustomAttrValue('');
    }
  };

  const handleRemoveCustomAttribute = (keyToRemove) => {
    setFormData(prev => {
      const newAttributes = { ...prev.custom_attributes };
      delete newAttributes[keyToRemove];
      return { ...prev, custom_attributes: newAttributes };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const endpoint = stakeholder 
        ? `/v2/stakeholders/${stakeholder.id}` 
        : '/v2/stakeholders';
      const method = stakeholder ? 'put' : 'post';

      await apiClient[method](endpoint, formData);
      onClose(true); // true indicates refresh needed
    } catch (err) {
      console.error('Error saving stakeholder:', err);
      setError(err.response?.data?.error || 'Failed to save stakeholder. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const renderProfileTab = () => (
    <div className="stakeholder-tab-content">
      <div className="form-section">
        <h4 className="section-title">
          <FaUser /> Basic Information
        </h4>
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="first_name">First Name *</label>
            <input
              type="text"
              id="first_name"
              value={formData.first_name}
              onChange={(e) => handleInputChange('first_name', e.target.value)}
              required
              disabled={loading}
            />
          </div>
          <div className="form-group">
            <label htmlFor="last_name">Last Name *</label>
            <input
              type="text"
              id="last_name"
              value={formData.last_name}
              onChange={(e) => handleInputChange('last_name', e.target.value)}
              required
              disabled={loading}
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="email">Email Address</label>
            <input
              type="email"
              id="email"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              disabled={loading}
            />
          </div>
          <div className="form-group">
            <label htmlFor="phone">Phone Number</label>
            <input
              type="tel"
              id="phone"
              value={formData.phone}
              onChange={(e) => handleInputChange('phone', e.target.value)}
              disabled={loading}
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="job_title">Job Title</label>
            <input
              type="text"
              id="job_title"
              value={formData.job_title}
              onChange={(e) => handleInputChange('job_title', e.target.value)}
              disabled={loading}
            />
          </div>
          <div className="form-group">
            <label htmlFor="department">Department</label>
            <input
              type="text"
              id="department"
              value={formData.department}
              onChange={(e) => handleInputChange('department', e.target.value)}
              disabled={loading}
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="organization">Organization</label>
            <input
              type="text"
              id="organization"
              value={formData.organization}
              onChange={(e) => handleInputChange('organization', e.target.value)}
              disabled={loading}
            />
          </div>
          <div className="form-group">
            <label htmlFor="location">Location</label>
            <input
              type="text"
              id="location"
              value={formData.location}
              onChange={(e) => handleInputChange('location', e.target.value)}
              disabled={loading}
              placeholder="City, State/Province, Country"
            />
          </div>
        </div>
      </div>
    </div>
  );

  const renderClassificationTab = () => (
    <div className="stakeholder-tab-content">
      <div className="form-section">
        <h4 className="section-title">
          <FaUserTie /> Stakeholder Classification
        </h4>
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="stakeholder_category">Stakeholder Category *</label>
            <select
              id="stakeholder_category"
              value={formData.stakeholder_category}
              onChange={(e) => handleInputChange('stakeholder_category', e.target.value)}
              disabled={loading}
              required
            >
              {stakeholderCategories.map(category => (
                <option key={category} value={category}>
                  {category.charAt(0).toUpperCase() + category.slice(1)}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label htmlFor="stakeholder_type">Stakeholder Type</label>
            <select
              id="stakeholder_type"
              value={formData.stakeholder_type}
              onChange={(e) => handleInputChange('stakeholder_type', e.target.value)}
              disabled={loading}
            >
              <option value="">Select Type</option>
              {stakeholderTypes.map(type => (
                <option key={type} value={type}>
                  {type.charAt(0).toUpperCase() + type.slice(1)}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="influence_level">Influence Level</label>
            <select
              id="influence_level"
              value={formData.influence_level}
              onChange={(e) => handleInputChange('influence_level', e.target.value)}
              disabled={loading}
            >
              <option value="">Select Level</option>
              {influenceLevels.map(level => (
                <option key={level} value={level}>
                  {level.charAt(0).toUpperCase() + level.slice(1)}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label htmlFor="interest_level">Interest Level</label>
            <select
              id="interest_level"
              value={formData.interest_level}
              onChange={(e) => handleInputChange('interest_level', e.target.value)}
              disabled={loading}
            >
              <option value="">Select Level</option>
              {interestLevels.map(level => (
                <option key={level} value={level}>
                  {level.charAt(0).toUpperCase() + level.slice(1)}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="attitude">Attitude</label>
            <select
              id="attitude"
              value={formData.attitude}
              onChange={(e) => handleInputChange('attitude', e.target.value)}
              disabled={loading}
            >
              <option value="">Select Attitude</option>
              {attitudeOptions.map(attitude => (
                <option key={attitude} value={attitude}>
                  {attitude.charAt(0).toUpperCase() + attitude.slice(1)}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label htmlFor="power_level">Power Level</label>
            <select
              id="power_level"
              value={formData.power_level}
              onChange={(e) => handleInputChange('power_level', e.target.value)}
              disabled={loading}
            >
              <option value="">Select Level</option>
              {powerLevels.map(level => (
                <option key={level} value={level}>
                  {level.charAt(0).toUpperCase() + level.slice(1)}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
    </div>
  );

  const renderEngagementTab = () => (
    <div className="stakeholder-tab-content">
      <div className="form-section">
        <h4 className="section-title">
          <FaComments /> Communication & Engagement
        </h4>
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="preferred_contact_method">Preferred Contact Method</label>
            <select
              id="preferred_contact_method"
              value={formData.preferred_contact_method}
              onChange={(e) => handleInputChange('preferred_contact_method', e.target.value)}
              disabled={loading}
            >
              {contactMethods.map(method => (
                <option key={method} value={method}>
                  {method.charAt(0).toUpperCase() + method.slice(1)}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label htmlFor="engagement_frequency">Engagement Frequency</label>
            <select
              id="engagement_frequency"
              value={formData.engagement_frequency}
              onChange={(e) => handleInputChange('engagement_frequency', e.target.value)}
              disabled={loading}
            >
              <option value="">Select Frequency</option>
              {engagementFrequencies.map(freq => (
                <option key={freq} value={freq}>
                  {freq.charAt(0).toUpperCase() + freq.slice(1)}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="communication_preference">Communication Preference</label>
          <input
            type="text"
            id="communication_preference"
            value={formData.communication_preference}
            onChange={(e) => handleInputChange('communication_preference', e.target.value)}
            disabled={loading}
            placeholder="e.g., formal emails, meetings, reports"
          />
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="key_concerns">Key Concerns</label>
            <textarea
              id="key_concerns"
              value={formData.key_concerns}
              onChange={(e) => handleInputChange('key_concerns', e.target.value)}
              disabled={loading}
              rows="3"
              placeholder="What are this stakeholder's main concerns?"
            />
          </div>
          <div className="form-group">
            <label htmlFor="value_proposition">Value Proposition</label>
            <textarea
              id="value_proposition"
              value={formData.value_proposition}
              onChange={(e) => handleInputChange('value_proposition', e.target.value)}
              disabled={loading}
              rows="3"
              placeholder="What value does the change bring to this stakeholder?"
            />
          </div>
        </div>
      </div>
    </div>
  );

  const renderAdditionalTab = () => (
    <div className="stakeholder-tab-content">
      <div className="form-section">
        <h4 className="section-title">Demographics (Optional)</h4>
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="gender">Gender</label>
            <input
              type="text"
              id="gender"
              value={formData.gender}
              onChange={(e) => handleInputChange('gender', e.target.value)}
              disabled={loading}
              placeholder="Optional"
            />
          </div>
          <div className="form-group">
            <label htmlFor="age_range">Age Range</label>
            <select
              id="age_range"
              value={formData.age_range}
              onChange={(e) => handleInputChange('age_range', e.target.value)}
              disabled={loading}
            >
              <option value="">Select Range</option>
              {ageRanges.map(range => (
                <option key={range} value={range}>{range}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="tenure">Tenure</label>
          <select
            id="tenure"
            value={formData.tenure}
            onChange={(e) => handleInputChange('tenure', e.target.value)}
            disabled={loading}
          >
            <option value="">Select Tenure</option>
            {tenureOptions.map(tenure => (
              <option key={tenure} value={tenure}>{tenure}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="form-section">
        <h4 className="section-title">
          <FaTag /> Custom Tags
        </h4>
        <div className="tags-input-group">
          <input
            type="text"
            value={currentTagInput}
            onChange={(e) => setCurrentTagInput(e.target.value)}
            placeholder="Add a tag..."
            disabled={loading}
            onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
          />
          <button type="button" onClick={handleAddTag} disabled={loading}>
            Add Tag
          </button>
        </div>
        {formData.custom_tags.length > 0 && (
          <div className="tags-display">
            {formData.custom_tags.map((tag, index) => (
              <span key={index} className="tag">
                {tag}
                <button
                  type="button"
                  onClick={() => handleRemoveTag(tag)}
                  disabled={loading}
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      <div className="form-section">
        <h4 className="section-title">Custom Attributes</h4>
        <div className="custom-attr-input-group">
          <input
            type="text"
            value={customAttrKey}
            onChange={(e) => setCustomAttrKey(e.target.value)}
            placeholder="Attribute name..."
            disabled={loading}
          />
          <input
            type="text"
            value={customAttrValue}
            onChange={(e) => setCustomAttrValue(e.target.value)}
            placeholder="Attribute value..."
            disabled={loading}
          />
          <button type="button" onClick={handleAddCustomAttribute} disabled={loading}>
            Add
          </button>
        </div>
        {Object.keys(formData.custom_attributes).length > 0 && (
          <div className="custom-attributes-display">
            {Object.entries(formData.custom_attributes).map(([key, value]) => (
              <div key={key} className="custom-attribute">
                <span className="attr-key">{key}:</span>
                <span className="attr-value">{value}</span>
                <button
                  type="button"
                  onClick={() => handleRemoveCustomAttribute(key)}
                  disabled={loading}
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="form-section">
        <h4 className="section-title">Notes</h4>
        <div className="form-group">
          <textarea
            id="notes"
            value={formData.notes}
            onChange={(e) => handleInputChange('notes', e.target.value)}
            disabled={loading}
            rows="4"
            placeholder="Additional notes about this stakeholder..."
          />
        </div>
      </div>
    </div>
  );

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content stakeholder-profile-modal">
        <div className="modal-header">
          <h3>
            <FaUser /> 
            {stakeholder ? 'Edit Stakeholder Profile' : 'Add New Stakeholder'}
          </h3>
          <button className="close-btn" onClick={() => onClose(false)}>
            <FaTimes />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="stakeholder-form">
          {/* Tab Navigation */}
          <div className="tab-navigation">
            {tabs.map(tab => {
              const IconComponent = tab.icon;
              return (
                <button
                  key={tab.id}
                  type="button"
                  className={`tab-button ${activeTab === tab.id ? 'active' : ''}`}
                  onClick={() => setActiveTab(tab.id)}
                  disabled={loading}
                >
                  <IconComponent />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </div>

          <div className="modal-body">
            {error && <div className="error-message">{error}</div>}

            {/* Tab Content */}
            {activeTab === 'profile' && renderProfileTab()}
            {activeTab === 'classification' && renderClassificationTab()}
            {activeTab === 'engagement' && renderEngagementTab()}
            {activeTab === 'additional' && renderAdditionalTab()}
          </div>

          <div className="modal-footer">
            <button type="button" className="btn secondary" onClick={() => onClose(false)} disabled={loading}>
              Cancel
            </button>
            <button type="submit" className="btn primary" disabled={loading}>
              {loading ? 'Saving...' : (stakeholder ? 'Update Profile' : 'Create Profile')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default StakeholderProfileModal; 
